import React, { useCallback, useState } from "react";
import MediaRenderer from "../components/MediaRenderer";

/**
 * 🎬 useMediaRenderer - Hook avançado para gerenciar renderização de mídias
 * 
 * Centraliza a lógica de renderização de diferentes tipos de mídia,
 * fornecendo uma interface simples e robusta para o componente MessagesList.
 * 
 * Recursos:
 * - Renderização otimizada de todos os tipos de mídia
 * - Tratamento de erros centralizado
 * - Estados de carregamento
 * - Callbacks para eventos de mídia
 * - Performance otimizada com memoização
 * 
 * @returns {Object} - Objeto com função de renderização e estados
 */
export const useMediaRenderer = (messagesList = []) => {
  // 🏗️ Estados para controle de mídia
  const [loadingStates, setLoadingStates] = useState({});
  const [errorStates, setErrorStates] = useState({});

  // 🎯 Handler de erro de mídia
  const handleMediaError = useCallback((messageId, error) => {
    setErrorStates(prev => ({
      ...prev,
      [messageId]: error
    }));
    setLoadingStates(prev => ({
      ...prev,
      [messageId]: false
    }));
  }, []);

  // 🎯 Handler de carregamento de mídia
  const handleMediaLoad = useCallback((messageId) => {
    setLoadingStates(prev => ({
      ...prev,
      [messageId]: false
    }));
    setErrorStates(prev => {
      const newState = { ...prev };
      delete newState[messageId];
      return newState;
    });
  }, []);

  // 🎯 Handler de início de carregamento
  const handleMediaLoadStart = useCallback((messageId) => {
    setLoadingStates(prev => ({
      ...prev,
      [messageId]: true
    }));
  }, []);

  // 🎯 Função memoizada para renderizar mídia
  const buildImageSequence = useCallback((startMessageId) => {
    try {
      const items = (messagesList || [])
        .filter(m => m && m.mediaType === 'image' && m.mediaUrl)
        .sort((a, b) => {
          const ta = new Date(a.createdAt || a.timestamp || 0).getTime();
          const tb = new Date(b.createdAt || b.timestamp || 0).getTime();
          if (ta !== tb) return ta - tb;
          return String(a.id).localeCompare(String(b.id));
        })
        .map(m => ({
          id: m.id,
          url: m.mediaUrl,
          title: m.filename || m.originalName || 'Imagem',
          type: 'image',
        }));
      const startIndex = Math.max(0, items.findIndex(it => it.id === startMessageId));
      return { items, startIndex: startIndex === -1 ? 0 : startIndex };
    } catch {
      return { items: [], startIndex: 0 };
    }
  }, [messagesList]);

  const renderMedia = useCallback((message) => {
    // Verificar se a mensagem tem mídia
    // Permitir locationMessage e contactMessage mesmo sem mediaUrl (dados ficam no body)
    if (!message?.mediaType || 
        (!message?.mediaUrl && 
         message.mediaType !== "locationMessage" && 
         message.mediaType !== "contactMessage")) {
      return null;
    }

    const messageId = message.id || message.uuid || `${message.mediaType}-${Date.now()}`;

    // Iniciar carregamento se ainda não foi iniciado
    if (loadingStates[messageId] === undefined) {
      handleMediaLoadStart(messageId);
    }

    // Renderizar usando o componente MediaRenderer otimizado
    return (
      <MediaRenderer 
        key={messageId}
        message={message}
        onError={(error) => handleMediaError(messageId, error)}
        onLoad={() => handleMediaLoad(messageId)}
        buildImageSequence={buildImageSequence}
      />
    );
  }, [loadingStates, handleMediaError, handleMediaLoad, handleMediaLoadStart, buildImageSequence]);

  // 🎯 Função para limpar estados de uma mensagem
  const clearMediaState = useCallback((messageId) => {
    setLoadingStates(prev => {
      const newState = { ...prev };
      delete newState[messageId];
      return newState;
    });
    setErrorStates(prev => {
      const newState = { ...prev };
      delete newState[messageId];
      return newState;
    });
  }, []);

  // 🎯 Função para limpar todos os estados
  const clearAllMediaStates = useCallback(() => {
    setLoadingStates({});
    setErrorStates({});
  }, []);

  // 🎯 Função para verificar se uma mídia está carregando
  const isMediaLoading = useCallback((messageId) => {
    return Boolean(loadingStates[messageId]);
  }, [loadingStates]);

  // 🎯 Função para verificar se uma mídia tem erro
  const hasMediaError = useCallback((messageId) => {
    return Boolean(errorStates[messageId]);
  }, [errorStates]);

  // 🎯 Função para obter erro de uma mídia
  const getMediaError = useCallback((messageId) => {
    return errorStates[messageId] || null;
  }, [errorStates]);

  // 🎯 Estatísticas de mídia
  const mediaStats = {
    totalLoading: Object.keys(loadingStates).filter(id => loadingStates[id]).length,
    totalErrors: Object.keys(errorStates).length,
    hasAnyLoading: Object.values(loadingStates).some(Boolean),
    hasAnyErrors: Object.keys(errorStates).length > 0,
  };

  return {
    // Função principal de renderização
    renderMedia,
    buildImageSequence,
    
    // Funções de controle de estado
    clearMediaState,
    clearAllMediaStates,
    
    // Funções de verificação
    isMediaLoading,
    hasMediaError,
    getMediaError,
    
    // Estatísticas
    mediaStats,
    
    // Estados brutos (para debug)
    loadingStates,
    errorStates,
  };
};

export default useMediaRenderer;
